import { supabase } from './supabase';
import { format, subDays } from 'date-fns';

// ── Types ───────────────────────────────────────────────────

export interface DSRStatusToday {
  submitted: number;
  draft: number;
  notStarted: number;
  total: number;
}

export interface ProjectBreakdownRow {
  projectId: string;
  projectName: string;
  dsrsTodayCount: number;
  latestStatus: string | null;
  workforce: number;
  safetyEvents30d: number;
  swaEvents30d: number;
  lastSubmitted: string | null;
  // S80: additive fields consumed by Executive Active Projects cards
  // (AC 12.1.7) and Safety Manager project breakdown (AC 12.1.5).
  businessUnitName: string | null;
  divisionName: string | null;
  currentLocation: string | null;
  lastRiskLevel: string | null;
}

export interface SafetyEventRow {
  id: string;
  dsrReportId: string;
  eventDatetime: string;
  projectName: string;
  eventType: string;
  eventTitle: string;
  category: string;
  createdByName: string;
}

export interface GoodCatchRow {
  id: string;
  dsrReportId: string;
  createdAt: string;
  projectName: string;
  observationCategory: string;
  description: string;
}

// ── Helpers ─────────────────────────────────────────────────

const todayStr = () => format(new Date(), 'yyyy-MM-dd');
const daysAgoStr = (days: number) => format(subDays(new Date(), days), 'yyyy-MM-dd');

// ── Queries ─────────────────────────────────────────────────

export async function getDSRStatusToday(tenantId: string): Promise<DSRStatusToday> {
  // Get today's DSRs
  const { data: dsrs, error: dsrErr } = await supabase
    .from('dsr_reports')
    .select('status')
    .eq('is_deleted', false)
    .eq('report_date', todayStr());
  if (dsrErr) throw dsrErr;

  // Get active project count for "not started"
  const { count: projectCount, error: pErr } = await supabase
    .from('projects')
    .select('id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .eq('is_deleted', false);
  if (pErr) throw pErr;

  const rows = dsrs ?? [];
  const submitted = rows.filter((r) => ['submitted', 'reviewed', 'flagged', 'amended'].includes(r.status)).length;
  const draft = rows.filter((r) => r.status === 'draft').length;
  const activeProjects = projectCount ?? 0;
  const notStarted = Math.max(0, activeProjects - rows.length);

  return { submitted, draft, notStarted, total: rows.length };
}

export async function getActiveWorkforcePOB(_tenantId: string): Promise<number> {
  const { data: dsrs, error: dsrErr } = await supabase
    .from('dsr_reports')
    .select('id')
    .eq('is_deleted', false)
    .eq('report_date', todayStr());
  if (dsrErr) throw dsrErr;
  if (!dsrs?.length) return 0;

  const dsrIds = dsrs.map((d) => d.id);
  const { data: personnel, error: pErr } = await supabase
    .from('dsr_personnel_summaries')
    .select('personnel_count')
    .eq('is_deleted', false)
    .in('dsr_report_id', dsrIds);
  if (pErr) throw pErr;

  return (personnel ?? []).reduce((sum, r) => sum + (r.personnel_count ?? 0), 0);
}

export async function getSafetyEventCount(_tenantId: string, days: number): Promise<number> {
  const { count, error } = await supabase
    .from('dsr_safety_events')
    .select('id', { count: 'exact', head: true })
    .eq('is_deleted', false)
    .gte('event_datetime', daysAgoStr(days));
  if (error) throw error;
  return count ?? 0;
}

export async function getSWAEventCount(_tenantId: string, days: number): Promise<number> {
  const { count, error } = await supabase
    .from('dsr_swa_events')
    .select('id', { count: 'exact', head: true })
    .eq('is_deleted', false)
    .gte('invoked_at', daysAgoStr(days));
  if (error) throw error;
  return count ?? 0;
}

/**
 * Per-project rollup for the Safety Manager project breakdown table and
 * the Executive Active Projects cards.
 *
 * Returns DSR status today, 30d safety/SWA counts, headcount, last
 * submitted timestamp, and — added in S80 — business unit name,
 * division name, current active location name, and last submitted
 * DSR's overall_risk_level. The org-hierarchy fields join through
 * projects → divisions → business_units and the active
 * project_locations → locations record. The risk level comes from the
 * most recent submitted dsr_reports.overall_risk_level for the project.
 * Any of the additive fields return null when the source row is absent.
 */
export async function getProjectBreakdown(tenantId: string): Promise<ProjectBreakdownRow[]> {
  // Get active projects with joined org hierarchy (division → BU)
  const { data: projects, error: pErr } = await supabase
    .from('projects')
    .select('id, name, divisions:division_id(name, business_units:business_unit_id(name))')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .eq('is_deleted', false)
    .order('name');
  if (pErr) throw pErr;
  if (!projects?.length) return [];

  const projectIds = (projects as { id: string }[]).map((p) => p.id);
  const today = todayStr();
  const thirtyDaysAgo = daysAgoStr(30);

  // Fetch all data in parallel
  const [dsrsRes, personnelRes, safetyRes, swaRes, lastSubmittedRes, activeLocRes] = await Promise.all([
    // Today's DSRs
    supabase
      .from('dsr_reports')
      .select('id, project_id, status')
      .eq('is_deleted', false)
      .eq('report_date', today)
      .in('project_id', projectIds),
    // Today's personnel
    supabase
      .from('dsr_reports')
      .select('project_id, dsr_personnel_summaries(personnel_count)')
      .eq('is_deleted', false)
      .eq('report_date', today)
      .in('project_id', projectIds),
    // Safety events last 30 days
    supabase
      .from('dsr_safety_events')
      .select('id, dsr_report_id')
      .eq('is_deleted', false)
      .gte('event_datetime', thirtyDaysAgo),
    // SWA events last 30 days
    supabase
      .from('dsr_swa_events')
      .select('id, dsr_report_id')
      .eq('is_deleted', false)
      .gte('invoked_at', thirtyDaysAgo),
    // Last submitted DSR per project — overall_risk_level rides along
    supabase
      .from('dsr_reports')
      .select('project_id, submitted_at, overall_risk_level')
      .eq('is_deleted', false)
      .in('status', ['submitted', 'reviewed', 'flagged', 'amended'])
      .in('project_id', projectIds)
      .order('submitted_at', { ascending: false }),
    // Active project_location per project (one per project per ARCH-003
    // enforced by partial unique index project_locations_one_active).
    supabase
      .from('project_locations')
      .select('project_id, locations:location_id(name)')
      .in('project_id', projectIds)
      .eq('is_active', true)
      .eq('is_deleted', false),
  ]);

  const allDSRs = dsrsRes.data ?? [];

  // Map DSR report_id -> project_id for events (need all DSRs in last 30 days)
  const { data: dsrsLast30, error: dsrMapErr } = await supabase
    .from('dsr_reports')
    .select('id, project_id')
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', thirtyDaysAgo);
  if (dsrMapErr) throw dsrMapErr;

  const dsrToProject = new Map<string, string>();
  (dsrsLast30 ?? []).forEach((d) => dsrToProject.set(d.id, d.project_id));

  // project_id -> location name
  const locationByProject = new Map<string, string | null>();
  for (const r of (activeLocRes.data ?? []) as any[]) {
    locationByProject.set(r.project_id, r.locations?.name ?? null);
  }

  return (projects as any[]).map((project) => {
    const todayDsrs = allDSRs.filter((d) => d.project_id === project.id);
    const latestStatus = todayDsrs.length > 0 ? todayDsrs[0].status : null;

    const personnelRows = (personnelRes.data ?? [])
      .filter((d: any) => d.project_id === project.id)
      .flatMap((d: any) => d.dsr_personnel_summaries ?? []);
    const workforce = personnelRows.reduce((sum: number, r: any) => sum + (r.personnel_count ?? 0), 0);

    const safetyEvents30d = (safetyRes.data ?? [])
      .filter((e) => dsrToProject.get(e.dsr_report_id) === project.id).length;
    const swaEvents30d = (swaRes.data ?? [])
      .filter((e) => dsrToProject.get(e.dsr_report_id) === project.id).length;

    const lastSubmittedRow = (lastSubmittedRes.data ?? [])
      .find((d) => d.project_id === project.id);

    const divisionName = project.divisions?.name ?? null;
    const businessUnitName = project.divisions?.business_units?.name ?? null;

    return {
      projectId: project.id,
      projectName: project.name,
      dsrsTodayCount: todayDsrs.length,
      latestStatus,
      workforce,
      safetyEvents30d,
      swaEvents30d,
      lastSubmitted: lastSubmittedRow?.submitted_at ?? null,
      businessUnitName,
      divisionName,
      currentLocation: locationByProject.get(project.id) ?? null,
      lastRiskLevel: lastSubmittedRow?.overall_risk_level ?? null,
    };
  });
}

export async function getRecentSafetyEvents(_tenantId: string, limit: number): Promise<SafetyEventRow[]> {
  const { data, error } = await supabase
    .from('dsr_safety_events')
    .select(`
      id, dsr_report_id, event_datetime, event_type, event_title, category,
      dsr_reports!inner ( projects!inner ( name ) ),
      users:created_by ( first_name, last_name )
    `)
    .eq('is_deleted', false)
    .order('event_datetime', { ascending: false })
    .limit(limit);
  if (error) throw error;

  return (data ?? []).map((row: any) => ({
    id: row.id,
    dsrReportId: row.dsr_report_id,
    eventDatetime: row.event_datetime,
    projectName: row.dsr_reports?.projects?.name ?? '—',
    eventType: row.event_type,
    eventTitle: row.event_title,
    category: row.category,
    createdByName: row.users
      ? `${row.users.first_name} ${row.users.last_name}`
      : '—',
  }));
}

export async function getRecentGoodCatch(_tenantId: string, limit: number): Promise<GoodCatchRow[]> {
  const { data, error } = await supabase
    .from('dsr_hse_observations')
    .select(`
      id, dsr_report_id, created_at, observation_category, description,
      dsr_reports!inner ( projects!inner ( name ) )
    `)
    .eq('is_deleted', false)
    .eq('observation_type', 'positive')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;

  return (data ?? []).map((row: any) => ({
    id: row.id,
    dsrReportId: row.dsr_report_id,
    createdAt: row.created_at,
    projectName: row.dsr_reports?.projects?.name ?? '—',
    observationCategory: row.observation_category ?? 'Other',
    description: row.description,
  }));
}

// ════════════════════════════════════════════════════════════
//  S77 — Gap-fill widget queries (Executive / SM / Supervisor)
// ════════════════════════════════════════════════════════════

const SUBMITTED_STATUSES = ['submitted', 'reviewed', 'flagged', 'amended'];

// ── Operations Snapshot ─────────────────────────────────────

export interface TenantOperationsSnapshot {
  activeProjects: number;
  activeLocations: number;
  ssrsOnAssignment: number;
  dsrsSubmittedToday: number;
  submittedDsrsLast30d: number;
}

/**
 * Tenant-wide headline counts for the Executive Operations Status row
 * and Safety Manager Portfolio Overview row.
 *
 * Counts: active projects, active project_locations, SSRs with at least
 * one active project assignment, DSRs submitted today, DSRs submitted in
 * the last 30 days.
 */
export async function getTenantOperationsSnapshot(
  tenantId: string,
): Promise<TenantOperationsSnapshot> {
  const today = todayStr();
  const thirtyDaysAgo = daysAgoStr(30);

  const [projRes, locRes, ssrRes, todayRes, last30Res] = await Promise.all([
    supabase
      .from('projects')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .eq('is_deleted', false),
    supabase
      .from('project_locations')
      .select('location_id')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .eq('is_deleted', false),
    supabase
      .from('user_project_assignments')
      .select('user_id')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .eq('is_deleted', false),
    supabase
      .from('dsr_reports')
      .select('id', { count: 'exact', head: true })
      .eq('is_deleted', false)
      .eq('report_date', today)
      .in('status', SUBMITTED_STATUSES),
    supabase
      .from('dsr_reports')
      .select('id', { count: 'exact', head: true })
      .eq('is_deleted', false)
      .gte('report_date', thirtyDaysAgo)
      .in('status', SUBMITTED_STATUSES),
  ]);

  const activeLocations = new Set(
    (locRes.data ?? []).map((r) => (r as { location_id: string }).location_id),
  ).size;
  const ssrsOnAssignment = new Set(
    (ssrRes.data ?? []).map((r) => (r as { user_id: string }).user_id),
  ).size;

  return {
    activeProjects: projRes.count ?? 0,
    activeLocations,
    ssrsOnAssignment,
    dsrsSubmittedToday: todayRes.count ?? 0,
    submittedDsrsLast30d: last30Res.count ?? 0,
  };
}

// ── Risk distribution ──────────────────────────────────────

export interface RiskDistribution {
  high: number;
  medium: number;
  low: number;
  notAssessed: number;
  total: number;
  highPct: number;
  mediumPct: number;
  lowPct: number;
  notAssessedPct: number;
}

function emptyRiskDistribution(): RiskDistribution {
  return {
    high: 0, medium: 0, low: 0, notAssessed: 0, total: 0,
    highPct: 0, mediumPct: 0, lowPct: 0, notAssessedPct: 0,
  };
}

function buildRiskDistribution(
  rows: { overall_risk_level: string | null; summary?: string | null }[],
): RiskDistribution {
  let high = 0, medium = 0, low = 0, notAssessed = 0;
  for (const row of rows) {
    const risk = row.overall_risk_level ?? row.summary ?? null;
    if (risk === 'high' || risk === 'critical') high += 1;
    else if (risk === 'medium') medium += 1;
    else if (risk === 'low') low += 1;
    else notAssessed += 1;
  }
  const total = high + medium + low + notAssessed;
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 1000) / 10 : 0);
  return {
    high, medium, low, notAssessed, total,
    highPct: pct(high),
    mediumPct: pct(medium),
    lowPct: pct(low),
    notAssessedPct: pct(notAssessed),
  };
}

/**
 * DSR risk-level distribution for the period. Used by the Executive
 * Portfolio Risk widget and the Safety Manager Risk Distribution widget.
 *
 * Risk source-of-truth (post-ARCH-030): dsr_reports.overall_risk_level,
 * with fallback to dsr_activities_summaries.overall_risk_level for
 * legacy DSRs that predate the composite column.
 */
export async function getPortfolioRiskDistribution(
  projectIds: string[],
  days: number,
): Promise<RiskDistribution> {
  if (projectIds.length === 0) return emptyRiskDistribution();
  const start = daysAgoStr(days);

  const { data, error } = await supabase
    .from('dsr_reports')
    .select(
      'overall_risk_level, dsr_activities_summaries(overall_risk_level)',
    )
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .in('status', SUBMITTED_STATUSES)
    .gte('report_date', start);
  if (error) throw error;

  const rows = ((data ?? []) as any[]).map((r) => ({
    overall_risk_level: r.overall_risk_level ?? null,
    summary: (r.dsr_activities_summaries ?? [])[0]?.overall_risk_level ?? null,
  }));
  return buildRiskDistribution(rows);
}

/**
 * Same shape as getPortfolioRiskDistribution but scoped to a single day —
 * used by the Supervisor "Overall Risk Distribution Today" widget.
 */
export async function getRiskDistributionToday(
  projectIds: string[],
  date?: string,
): Promise<RiskDistribution> {
  if (projectIds.length === 0) return emptyRiskDistribution();
  const reportDate = date ?? todayStr();

  const { data, error } = await supabase
    .from('dsr_reports')
    .select(
      'overall_risk_level, dsr_activities_summaries(overall_risk_level)',
    )
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .eq('report_date', reportDate);
  if (error) throw error;

  const rows = ((data ?? []) as any[]).map((r) => ({
    overall_risk_level: r.overall_risk_level ?? null,
    summary: (r.dsr_activities_summaries ?? [])[0]?.overall_risk_level ?? null,
  }));
  return buildRiskDistribution(rows);
}

// ── Safety Events analysis ─────────────────────────────────

export interface SafetyEventTypeCounts {
  near_miss: number;
  first_aid: number;
  recordable_injury: number;
  lost_time_injury: number;
  property_damage: number;
  environmental_release: number;
  security_incident: number;
  total: number;
}

export type NearMissSeverity =
  | 'catastrophic' | 'critical' | 'serious' | 'moderate' | 'minor';

export interface SafetyEventsAnalysis {
  current: SafetyEventTypeCounts;
  previous: SafetyEventTypeCounts;
  trend: 'up' | 'down' | 'flat';
  highestSeverity: NearMissSeverity | null;
  projectsWithLTI: { projectId: string; projectName: string; count: number }[];
}

const EMPTY_TYPE_COUNTS: SafetyEventTypeCounts = {
  near_miss: 0, first_aid: 0, recordable_injury: 0, lost_time_injury: 0,
  property_damage: 0, environmental_release: 0, security_incident: 0, total: 0,
};

const SEVERITY_ORDER: NearMissSeverity[] = [
  'catastrophic', 'critical', 'serious', 'moderate', 'minor',
];

function bucketSafetyType(rows: { event_type: string }[]): SafetyEventTypeCounts {
  const c: SafetyEventTypeCounts = { ...EMPTY_TYPE_COUNTS };
  for (const r of rows) {
    const t = r.event_type;
    if (t === 'near_miss') c.near_miss += 1;
    else if (t === 'first_aid') c.first_aid += 1;
    else if (t === 'recordable_injury') c.recordable_injury += 1;
    else if (t === 'lost_time_injury') c.lost_time_injury += 1;
    else if (t === 'property_damage') c.property_damage += 1;
    else if (t === 'environmental_release') c.environmental_release += 1;
    else if (t === 'security_incident') c.security_incident += 1;
    else continue;
    c.total += 1;
  }
  return c;
}

/**
 * Combined Safety Events analytic for the period: type breakdown,
 * previous-period type breakdown for trend computation, highest severity
 * (near-miss potential_severity ENUM) seen in the period, and per-project
 * LTI counts.
 *
 * Note: safety_event_type ENUM does not include 'fatality' — LTI is the
 * closest proxy for the fatality projects-list need (S77 data gap).
 *
 * Time windows: pass `days` for both current and previous-period
 * comparison. The previous period spans the same length immediately
 * before the current window.
 */
export async function getSafetyEventsAnalysis(
  projectIds: string[],
  days: number,
): Promise<SafetyEventsAnalysis> {
  const empty: SafetyEventsAnalysis = {
    current: { ...EMPTY_TYPE_COUNTS },
    previous: { ...EMPTY_TYPE_COUNTS },
    trend: 'flat',
    highestSeverity: null,
    projectsWithLTI: [],
  };
  if (projectIds.length === 0) return empty;

  const currentStart = daysAgoStr(days);
  const previousStart = daysAgoStr(days * 2);

  // Pull DSRs across the previous + current window so events can be
  // mapped event → DSR → project. One DSR query covers both periods.
  const { data: dsrs, error: dErr } = await supabase
    .from('dsr_reports')
    .select('id, project_id, projects!inner(name)')
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', previousStart);
  if (dErr) throw dErr;

  const dsrIds = (dsrs ?? []).map((d) => d.id);
  const dsrToProject = new Map<string, { id: string; name: string }>();
  for (const d of (dsrs ?? []) as any[]) {
    dsrToProject.set(d.id, { id: d.project_id, name: d.projects?.name ?? '—' });
  }
  if (dsrIds.length === 0) return empty;

  const { data: events, error: eErr } = await supabase
    .from('dsr_safety_events')
    .select('event_type, event_datetime, dsr_report_id, near_miss_potential_severity')
    .eq('is_deleted', false)
    .in('dsr_report_id', dsrIds)
    .gte('event_datetime', previousStart);
  if (eErr) throw eErr;

  const currentRows: { event_type: string }[] = [];
  const previousRows: { event_type: string }[] = [];
  const ltiByProject = new Map<string, { name: string; count: number }>();
  let highest: NearMissSeverity | null = null;

  for (const row of (events ?? []) as any[]) {
    const inCurrent = row.event_datetime >= currentStart;
    if (inCurrent) {
      currentRows.push({ event_type: row.event_type });
      if (row.event_type === 'lost_time_injury') {
        const proj = dsrToProject.get(row.dsr_report_id);
        if (proj) {
          const existing = ltiByProject.get(proj.id);
          if (existing) existing.count += 1;
          else ltiByProject.set(proj.id, { name: proj.name, count: 1 });
        }
      }
      const sev = row.near_miss_potential_severity as NearMissSeverity | null;
      if (sev) {
        const sevRank = SEVERITY_ORDER.indexOf(sev);
        const curRank = highest ? SEVERITY_ORDER.indexOf(highest) : SEVERITY_ORDER.length;
        if (sevRank >= 0 && sevRank < curRank) highest = sev;
      }
    } else {
      previousRows.push({ event_type: row.event_type });
    }
  }

  const current = bucketSafetyType(currentRows);
  const previous = bucketSafetyType(previousRows);

  let trend: 'up' | 'down' | 'flat' = 'flat';
  if (previous.total > 0) {
    const change = (current.total - previous.total) / previous.total;
    if (change > 0.1) trend = 'up';
    else if (change < -0.1) trend = 'down';
  } else if (current.total > 0) {
    trend = 'up';
  }

  const projectsWithLTI = Array.from(ltiByProject.entries())
    .map(([id, v]) => ({ projectId: id, projectName: v.name, count: v.count }))
    .sort((a, b) => b.count - a.count);

  return { current, previous, trend, highestSeverity: highest, projectsWithLTI };
}

// ── Positive Observations summary ──────────────────────────

export interface PositiveObservationsSummary {
  total: number;
  avgPerDSR: number;
  topCategories: { category: string; count: number }[];
  dsrCount: number;
}

/**
 * Positive (Good Catch) HSE observations summary for the period.
 * Used by the Executive "Positive Observations" widget.
 *
 * Returns the total count, the average per DSR over the same period,
 * and the top 3 observation_category values by frequency.
 */
export async function getPositiveObservationsSummary(
  projectIds: string[],
  days: number,
): Promise<PositiveObservationsSummary> {
  const empty: PositiveObservationsSummary = {
    total: 0, avgPerDSR: 0, topCategories: [], dsrCount: 0,
  };
  if (projectIds.length === 0) return empty;
  const start = daysAgoStr(days);

  const { data: dsrs, error: dErr } = await supabase
    .from('dsr_reports')
    .select('id')
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', start);
  if (dErr) throw dErr;
  const dsrIds = (dsrs ?? []).map((d) => d.id);
  const dsrCount = dsrIds.length;
  if (dsrCount === 0) return empty;

  const { data: obs, error: oErr } = await supabase
    .from('dsr_hse_observations')
    .select('observation_category')
    .eq('is_deleted', false)
    .eq('observation_type', 'positive')
    .in('dsr_report_id', dsrIds);
  if (oErr) throw oErr;

  const rows = (obs ?? []) as { observation_category: string | null }[];
  const total = rows.length;
  const counts = new Map<string, number>();
  for (const r of rows) {
    const cat = r.observation_category ?? 'other';
    counts.set(cat, (counts.get(cat) ?? 0) + 1);
  }
  const topCategories = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([category, count]) => ({ category, count }));

  return {
    total,
    avgPerDSR: dsrCount > 0 ? Math.round((total / dsrCount) * 100) / 100 : 0,
    topCategories,
    dsrCount,
  };
}

// ── SWA Events analysis ────────────────────────────────────

export type SWASeverity = 'minor' | 'moderate' | 'serious' | 'critical';

export interface SWAEventsAnalysis {
  total: number;
  bySeverity: Record<SWASeverity, number>;
  projectsWithCritical: { projectId: string; projectName: string; count: number }[];
  openEventsByProject: {
    projectId: string;
    projectName: string;
    openCount: number;
    events: {
      id: string;
      invokedAt: string;
      hazardDescription: string;
      severity: SWASeverity;
      workStatus: string;
    }[];
  }[];
}

const EMPTY_SWA_SEVERITY: Record<SWASeverity, number> = {
  minor: 0, moderate: 0, serious: 0, critical: 0,
};

/**
 * Combined SWA Events analytic for the period: total count, severity
 * breakdown, projects that had a critical SWA fired, and open/ongoing
 * events grouped by project.
 *
 * "Open" = work_status IN ('stopped', 'ongoing'). 'resumed' is closed.
 */
export async function getSWAEventsAnalysis(
  projectIds: string[],
  days: number,
): Promise<SWAEventsAnalysis> {
  const empty: SWAEventsAnalysis = {
    total: 0,
    bySeverity: { ...EMPTY_SWA_SEVERITY },
    projectsWithCritical: [],
    openEventsByProject: [],
  };
  if (projectIds.length === 0) return empty;
  const start = daysAgoStr(days);

  const { data: dsrs, error: dErr } = await supabase
    .from('dsr_reports')
    .select('id, project_id, projects!inner(name)')
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', start);
  if (dErr) throw dErr;
  const dsrIds = (dsrs ?? []).map((d) => d.id);
  const dsrToProject = new Map<string, { id: string; name: string }>();
  for (const d of (dsrs ?? []) as any[]) {
    dsrToProject.set(d.id, { id: d.project_id, name: d.projects?.name ?? '—' });
  }
  if (dsrIds.length === 0) return empty;

  const { data: events, error: eErr } = await supabase
    .from('dsr_swa_events')
    .select('id, dsr_report_id, invoked_at, hazard_description, severity, work_status')
    .eq('is_deleted', false)
    .in('dsr_report_id', dsrIds)
    .gte('invoked_at', start);
  if (eErr) throw eErr;

  const bySeverity: Record<SWASeverity, number> = { ...EMPTY_SWA_SEVERITY };
  const criticalByProject = new Map<string, { name: string; count: number }>();
  const openByProject = new Map<string, {
    name: string;
    events: SWAEventsAnalysis['openEventsByProject'][number]['events'];
  }>();
  let total = 0;

  for (const row of (events ?? []) as any[]) {
    total += 1;
    const sev = row.severity as SWASeverity;
    if (sev in bySeverity) bySeverity[sev] += 1;
    const proj = dsrToProject.get(row.dsr_report_id);
    if (!proj) continue;
    if (sev === 'critical') {
      const existing = criticalByProject.get(proj.id);
      if (existing) existing.count += 1;
      else criticalByProject.set(proj.id, { name: proj.name, count: 1 });
    }
    if (row.work_status === 'stopped' || row.work_status === 'ongoing') {
      const list = openByProject.get(proj.id) ?? { name: proj.name, events: [] };
      list.events.push({
        id: row.id,
        invokedAt: row.invoked_at,
        hazardDescription: row.hazard_description,
        severity: sev,
        workStatus: row.work_status,
      });
      openByProject.set(proj.id, list);
    }
  }

  return {
    total,
    bySeverity,
    projectsWithCritical: Array.from(criticalByProject.entries())
      .map(([id, v]) => ({ projectId: id, projectName: v.name, count: v.count }))
      .sort((a, b) => b.count - a.count),
    openEventsByProject: Array.from(openByProject.entries())
      .map(([id, v]) => ({
        projectId: id,
        projectName: v.name,
        openCount: v.events.length,
        events: v.events.sort((a, b) => b.invokedAt.localeCompare(a.invokedAt)),
      }))
      .sort((a, b) => b.openCount - a.openCount),
  };
}

// ── High-risk activity summary ─────────────────────────────

export interface HighRiskActivitySummary {
  highCount: number;
  topRiskTags: { tag: string; count: number }[];
  projectsByHighCount: { projectId: string; projectName: string; highCount: number }[];
  daysWithSimopsCount: number;
}

/**
 * High-risk activity rollup for the period. Used by the Safety Manager
 * "High-Risk Activity Summary" widget.
 *
 * Counts dsr_activities rows where risk_level is 'high' or 'critical',
 * tallies the most-frequent risk_tags, ranks projects by high-risk
 * activity count, and counts distinct DSR report_dates where any DSR
 * had simops_detected = true on its activities summary.
 *
 * Performance note: the activity scan does an in-memory aggregation over
 * all activities in the window. If the window grows beyond ~10k DSRs in
 * Walk pilot phase a (project_id, risk_level, report_date) materialised
 * view would help — flag for a later session.
 */
export async function getHighRiskActivitySummary(
  projectIds: string[],
  days: number,
): Promise<HighRiskActivitySummary> {
  const empty: HighRiskActivitySummary = {
    highCount: 0, topRiskTags: [], projectsByHighCount: [], daysWithSimopsCount: 0,
  };
  if (projectIds.length === 0) return empty;
  const start = daysAgoStr(days);

  const { data: dsrs, error: dErr } = await supabase
    .from('dsr_reports')
    .select(`
      id, report_date, project_id, projects!inner(name),
      dsr_activities_summaries(simops_detected)
    `)
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', start);
  if (dErr) throw dErr;
  const dsrIds = (dsrs ?? []).map((d) => d.id);
  const dsrToProject = new Map<string, { id: string; name: string }>();
  const simopsDates = new Set<string>();
  for (const d of (dsrs ?? []) as any[]) {
    dsrToProject.set(d.id, { id: d.project_id, name: d.projects?.name ?? '—' });
    const summary = (d.dsr_activities_summaries ?? [])[0];
    if (summary?.simops_detected) simopsDates.add(d.report_date);
  }
  if (dsrIds.length === 0) return empty;

  const { data: acts, error: aErr } = await supabase
    .from('dsr_activities')
    .select('dsr_report_id, risk_level, risk_tags')
    .eq('is_deleted', false)
    .in('dsr_report_id', dsrIds);
  if (aErr) throw aErr;

  const tagCounts = new Map<string, number>();
  const projectHighCounts = new Map<string, { name: string; count: number }>();
  let highCount = 0;
  for (const a of (acts ?? []) as any[]) {
    if (a.risk_level === 'high' || a.risk_level === 'critical') {
      highCount += 1;
      const proj = dsrToProject.get(a.dsr_report_id);
      if (proj) {
        const existing = projectHighCounts.get(proj.id);
        if (existing) existing.count += 1;
        else projectHighCounts.set(proj.id, { name: proj.name, count: 1 });
      }
    }
    for (const tag of (a.risk_tags ?? []) as string[]) {
      tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
    }
  }

  return {
    highCount,
    topRiskTags: Array.from(tagCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([tag, count]) => ({ tag, count })),
    projectsByHighCount: Array.from(projectHighCounts.entries())
      .map(([id, v]) => ({ projectId: id, projectName: v.name, highCount: v.count }))
      .sort((a, b) => b.highCount - a.highCount),
    daysWithSimopsCount: simopsDates.size,
  };
}

// ── Permit compliance summary ──────────────────────────────

export interface PermitComplianceSummary {
  total: number;
  verifiedCount: number;
  expiredCount: number;
  byType: Record<string, number>;
}

/**
 * Permit compliance rollup for the period. Used by the Safety Manager
 * "Permit Compliance Summary" widget.
 *
 * Counts permits opened in the period (via DSR report_date scoping),
 * how many had SSR verification complete (is_verified = true), how
 * many are expired (expires_at in the past relative to now and status
 * not in 'closed'/'cancelled'), and a per-permit_type breakdown.
 */
export async function getPermitComplianceSummary(
  projectIds: string[],
  days: number,
): Promise<PermitComplianceSummary> {
  const empty: PermitComplianceSummary = {
    total: 0, verifiedCount: 0, expiredCount: 0, byType: {},
  };
  if (projectIds.length === 0) return empty;
  const start = daysAgoStr(days);

  const { data: dsrs, error: dErr } = await supabase
    .from('dsr_reports')
    .select('id')
    .eq('is_deleted', false)
    .in('project_id', projectIds)
    .gte('report_date', start);
  if (dErr) throw dErr;
  const dsrIds = (dsrs ?? []).map((d) => d.id);
  if (dsrIds.length === 0) return empty;

  const { data: permits, error: pErr } = await supabase
    .from('dsr_permit_entries')
    .select('permit_type, status, is_verified, expires_at')
    .eq('is_deleted', false)
    .in('dsr_report_id', dsrIds);
  if (pErr) throw pErr;

  const now = new Date().toISOString();
  const byType: Record<string, number> = {};
  let total = 0, verifiedCount = 0, expiredCount = 0;
  for (const row of (permits ?? []) as any[]) {
    total += 1;
    byType[row.permit_type] = (byType[row.permit_type] ?? 0) + 1;
    if (row.is_verified) verifiedCount += 1;
    const isClosed = row.status === 'closed' || row.status === 'cancelled';
    if (!isClosed && row.expires_at && row.expires_at < now) {
      expiredCount += 1;
    }
  }

  return { total, verifiedCount, expiredCount, byType };
}

// ── Projects with no DSR today ─────────────────────────────

export interface ProjectNoActivity {
  projectId: string;
  projectName: string;
}

/**
 * Returns projects in scope that have no DSR for the given date (any
 * status). Used by the Supervisor "Active DSRs Today" widget.
 */
export async function getProjectsWithNoDSRToday(
  projectIds: string[],
  date?: string,
): Promise<ProjectNoActivity[]> {
  if (projectIds.length === 0) return [];
  const reportDate = date ?? todayStr();

  const [projRes, dsrRes] = await Promise.all([
    supabase
      .from('projects')
      .select('id, name')
      .in('id', projectIds)
      .eq('is_active', true)
      .eq('is_deleted', false)
      .order('name'),
    supabase
      .from('dsr_reports')
      .select('project_id')
      .eq('is_deleted', false)
      .eq('report_date', reportDate)
      .in('project_id', projectIds),
  ]);
  if (projRes.error) throw projRes.error;
  if (dsrRes.error) throw dsrRes.error;

  const withDSR = new Set(
    (dsrRes.data ?? []).map((r) => (r as { project_id: string }).project_id),
  );
  return (projRes.data ?? [])
    .filter((p) => !withDSR.has(p.id))
    .map((p) => ({ projectId: p.id, projectName: p.name }));
}
